import type { CalculatorInputs, CalculationResults, PricingConfig } from '@/types/calculator';
import { CURRENCY_CONFIG, DEFAULT_PRICING_CONFIG, type SupportedCurrency } from './env';
import { getNetAnnualSavings, getROIPaybackTimeline } from './pricing-helpers';

/**
 * Detect user's preferred currency based on their locale
 */
export function detectUserCurrency(): SupportedCurrency {
  if (typeof window === 'undefined') return 'NZD'; // Default for SSR
  
  try {
    const locale = navigator.language || 'en-NZ';
    const region = locale.split('-')[1]?.toUpperCase();
    
    // Map common regions to currencies
    const regionToCurrency: Record<string, SupportedCurrency> = {
      'NZ': 'NZD',
      'AU': 'AUD', 
      'US': 'USD',
      'CA': 'CAD',
      'GB': 'GBP',
      'UK': 'GBP',
      'DE': 'EUR',
      'FR': 'EUR',
      'IT': 'EUR',
      'ES': 'EUR',
      'NL': 'EUR',
      'BE': 'EUR',
      'AT': 'EUR',
      'IE': 'EUR',
      'PT': 'EUR',
      'GR': 'EUR',
      'ZA': 'ZAR',
      'SE': 'EUR',
      'NO': 'EUR',
      'DK': 'EUR',
      'FI': 'EUR',
      'PL': 'EUR',
      'CZ': 'EUR',
      'RO': 'EUR',
      'BG': 'EUR',
      'HU': 'EUR',
      'HR': 'EUR',
      'SI': 'EUR',
      'ME': 'EUR',
      'AL': 'EUR',
      'MK': 'EUR',
      'RS': 'EUR'
    };
    
    return regionToCurrency[region] || 'NZD';
  } catch {
    return 'NZD'; // Fallback to default
  }
}

/**
 * Get pricing configuration for a specific currency
 */
export function getPricingForCurrency(currency: SupportedCurrency): PricingConfig {
  return DEFAULT_PRICING_CONFIG[currency];
}

/**
 * Format a number as currency in the specified currency
 */
export function formatCurrency(value: number, currency: SupportedCurrency = 'NZD'): string {
  const currencyInfo = CURRENCY_CONFIG.currencies[currency];
  
  // For currencies that use symbol prefix (all our supported currencies)
  const formatted = new Intl.NumberFormat('en', {
    minimumFractionDigits: currencyInfo.decimals,
    maximumFractionDigits: currencyInfo.decimals,
  }).format(Math.round(value));
  
  return `${currencyInfo.symbol}${formatted}`;
}


/**
 * Format currency with decimals when the value has fractional parts
 */
export function formatCurrencyWithDecimals(value: number, currency: SupportedCurrency = 'NZD'): string {
  const currencyInfo = CURRENCY_CONFIG.currencies[currency];
  
  // Check if the value has decimal places
  const hasDecimals = value % 1 !== 0;
  
  const formatted = new Intl.NumberFormat('en', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  }).format(value);
  
  return `${currencyInfo.symbol}${formatted}`;
}


/**
 * Calculate and format hourly rate from annual salary in the selected currency
 */
export function formatHourlyRate(annualSalary: number, currency: SupportedCurrency, hoursPerYear: number): string {
  const hourlyRate = annualSalary / hoursPerYear;
  const currencyInfo = CURRENCY_CONFIG.currencies[currency];
  
  // Format with 2 decimal places for hourly rates
  const formatted = new Intl.NumberFormat('en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(hourlyRate);
  
  return `${currencyInfo.symbol}${formatted}`;
}

export function formatNumber(value: number, decimals: number = 0): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatPercentage(value: number): string {
  return `${value}%`;
}

/**
 * Format numbers with K notation for readability
 * Examples: 1000 -> "1K", 30000 -> "30K", 1500 -> "1.5K"
 */
export function formatNumberWithK(value: number): string {
  if (value >= 1000) {
    const kValue = value / 1000;
    return kValue % 1 === 0 ? `${kValue}K` : `${kValue.toFixed(1)}K`;
  }
  return formatNumber(value);
}

/**
 * Format currency for per-request costs, showing cents when needed
 * Shows cents for values < $1, shows appropriate decimals for larger amounts
 */
export function formatCurrencyForPerRequest(value: number, currency: SupportedCurrency): string {
  const currencyInfo = CURRENCY_CONFIG.currencies[currency];
  
  let decimals = 0;
  if (value < 1) {
    // Show cents for values less than $1
    decimals = 2;
  } else if (value % 1 !== 0) {
    // Show decimals for non-whole dollar amounts >= $1
    decimals = 2;
  }
  
  const formatted = new Intl.NumberFormat('en', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
  
  return `${currencyInfo.symbol}${formatted}`;
}

export function calculateDerivedMetrics(inputs: CalculatorInputs) {
  const cardReplacementsPerYear = Math.round((inputs.cardholderDatabaseSize * inputs.cardReplacementPercentage) / 100);
  const newHiresPerYear = Math.round((inputs.cardholderDatabaseSize * inputs.staffTurnoverRate) / 100);
  const totalAccessRequests = inputs.cardholderDatabaseSize * inputs.accessRequestsPerCardholder;
  const annualVisitors = inputs.hasVisitorRegistration ? inputs.visitorsPerMonth * 12 : 0;
  return {
    cardReplacementsPerYear,
    newHiresPerYear,
    totalAccessRequests,
    annualVisitors,
  };
}

export function validateInputs(inputs: CalculatorInputs): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  
  // Check if at least one workflow is selected
  const hasAnyWorkflow = inputs.useCredentialRequests || inputs.useAccessRequests || inputs.useVisitorRequests;
  if (!hasAnyWorkflow) {
    errors.push('At least one workflow type must be selected');
  }
  
  if (inputs.cardholderAdmins <= 0) {
    errors.push('Number of cardholder administrators must be greater than 0');
  }
  
  if (inputs.costPerEmployee <= 0) {
    errors.push('Cost per employee must be greater than 0');
  }
  
  if (inputs.cardholderDatabaseSize <= 0) {
    errors.push('Total cardholders must be greater than 0');
  }
  
  // Only validate timePerRequest if credential or access workflows are enabled
  const needsTimePerRequest = inputs.useCredentialRequests || inputs.useAccessRequests;
  if (needsTimePerRequest) {
    if (inputs.timePerRequest <= 0) {
      errors.push('Time per request must be greater than 0');
    }
    
    if (inputs.accessNowTimePerRequest <= 0) {
      errors.push('AccessNow time per request must be greater than 0');
    }
    
    if (inputs.timePerRequest < inputs.accessNowTimePerRequest) {
      errors.push('Manual time per request cannot be less than AccessNow time per request');
    }
  }
  
  // Only validate visitor fields if visitor workflow is enabled
  if (inputs.useVisitorRequests && inputs.hasVisitorRegistration) {
    if (inputs.visitorsPerMonth <= 0) {
      errors.push('Visitors per month must be greater than 0 when visitor registration is enabled');
    }
    
    if (inputs.timePerVisitor <= 0) {
      errors.push('Time per visitor must be greater than 0 when visitor registration is enabled');
    }
    
    if (inputs.accessNowTimePerVisitor <= 0) {
      errors.push('AccessNow time per visitor must be greater than 0 when visitor registration is enabled');
    }
    
    if (inputs.timePerVisitor < inputs.accessNowTimePerVisitor) {
      errors.push('Manual time per visitor cannot be less than AccessNow time per visitor');
    }
  }
  
  
  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function getSimplifiedInputs(inputs: CalculatorInputs): Partial<CalculatorInputs> {
  return {
    cardholderDatabaseSize: inputs.cardholderDatabaseSize,
    cardholderAdmins: inputs.cardholderAdmins,
    costPerEmployee: inputs.costPerEmployee,
    accessRequestsPerCardholder: inputs.accessRequestsPerCardholder,
    hasVisitorRegistration: inputs.hasVisitorRegistration,
    visitorsPerMonth: inputs.visitorsPerMonth,
  };
}

export function getResultsSummary(results: CalculationResults) {
  const paybackInfo = getROIPaybackTimeline(results);
  return {
    totalSavings: results.totalSavings, // Keep for backward compatibility
    netAnnualSavings: getNetAnnualSavings(results),
    fteReduction: results.fteReduction,
    totalCost: results.accessNowPricing.totalCost,
    breaksEven: paybackInfo.hasPositiveSavings,
    breakEvenDescription: paybackInfo.description,
    workflowCount: results.accessNowPricing.workflows,
  };
}

export function getWizardSteps() {
  return [
    {
      id: 'intro',
      title: 'Welcome to AccessNow ROI Calculator',
      description: 'Calculate your return on investment for automating credentialing and access management workflows',
      fields: [] as const,
    },
    {
      id: 'workflow-selection',
      title: 'Select Your Workflows',
      description: 'Choose which workflow types you want to analyze',
      fields: ['useCredentialRequests', 'useAccessRequests', 'useVisitorRequests'] as const,
    },
    {
      id: 'organization',
      title: 'Organization Size',
      description: 'Tell us about your organization',
      fields: ['cardholderDatabaseSize', 'cardholderAdmins', 'costPerEmployee'] as const,
    },
    {
      id: 'risk-mitigation',
      title: 'Risk Mitigation Value',
      description: 'What value do you place on operational efficiency?',
      fields: ['operationalInsuranceValue'] as const,
    },
    {
      id: 'processes',
      title: 'Current Processes',
      description: 'How do you currently handle credentialing?',
      fields: ['timePerRequest'] as const,
    },
    {
      id: 'credentials',
      title: 'Credential Management',
      description: 'Configure your credential workflow settings',
      fields: ['cardReplacementPercentage', 'staffTurnoverRate', 'staffGrowthRate'] as const,
    },
    {
      id: 'access',
      title: 'Access Management',
      description: 'Configure your access request workflow',
      fields: ['accessRequestsPerCardholder'] as const,
    },
    {
      id: 'visitors',
      title: 'Visitor Management',
      description: 'Do you manage visitors on-site?',
      fields: ['hasVisitorRegistration', 'visitorsPerMonth'] as const,
    },
    {
      id: 'capacity',
      title: 'Capacity Planning',
      description: 'Plan for peak demand and workflow requirements',
      fields: ['extraRequestCapacityPercentage', 'differentiateEmployeeContractor'] as const,
    },
    {
      id: 'summary',
      title: 'Request Summary',
      description: 'Review your annual workflow volumes',
      fields: [] as const,
    },
    {
      id: 'results',
      title: 'Your ROI Analysis',
      description: 'Here\'s your potential savings with AccessNow',
      fields: [] as const,
    },
  ];
}

/**
 * Calculate suggested operational insurance value based on organization size
 */
export function getSuggestedOperationalInsuranceValue(cardholderDatabaseSize: number): number {
  if (cardholderDatabaseSize <= 200) return 1000;
  if (cardholderDatabaseSize <= 500) return 3000;
  if (cardholderDatabaseSize <= 1000) return 5000;
  if (cardholderDatabaseSize <= 5000) return 10000;
  if (cardholderDatabaseSize <= 10000) return 20000;
  if (cardholderDatabaseSize <= 20000) return 30000;
  return 50000; // Over 20,000
}

/**
 * Currency-aware pricing description helpers
 */
export function getPricingDescriptions(currency: SupportedCurrency, pricingConfig?: PricingConfig) {
  const config = pricingConfig || getPricingForCurrency(currency);
  return {
    workflow: `Includes 1,000 requests per workflow`,
    smallRequestPackage: `1,000 additional requests (${formatCurrency(config.extraRequestsSmallPrice, currency)} per package)`,
    largeRequestPackage: `30,000 additional requests (${formatCurrency(config.extraRequestsLargePrice, currency)} per package)`,
    workflowPrice: formatCurrency(config.workflowPrice, currency),
    smallRequestPackagePrice: formatCurrency(config.extraRequestsSmallPrice, currency),
    largeRequestPackagePrice: formatCurrency(config.extraRequestsLargePrice, currency),
  };
}